<script setup lang="ts">
import type { BookingExample } from '~/utils/examples/bookingExamples'
import CodeBlock from './CodeBlock.vue'

// One recipe on /examples/booking: what it is, when to use it, the thing
// running live, the block to copy, and the public page behind it.
defineProps<{
  example: BookingExample
  /** 1-based position, shown as a quiet index beside the heading. */
  index: number
}>()
</script>

<template>
  <section :id="example.id" class="example-section">
    <header class="example-head">
      <span class="example-index">{{ String(index).padStart(2, '0') }}</span>
      <div>
        <h2 class="example-title">
          <a :href="`#${example.id}`" class="example-anchor">{{ example.title }}</a>
        </h2>
        <p class="example-summary">{{ example.summary }}</p>
      </div>
    </header>

    <div v-if="$slots.demo" class="example-demo">
      <p class="example-demo-caption">{{ example.setup }}</p>
      <div class="example-demo-stage">
        <slot name="demo" />
      </div>
    </div>

    <CodeBlock :code="example.code" />

    <p v-if="example.notes" class="example-note">{{ example.notes }}</p>

    <p class="example-link">
      <a :href="example.publicUrl" target="_blank" rel="noopener">Open the booking page</a>
      <span class="example-link-url">{{ example.publicUrl }}</span>
    </p>
  </section>
</template>

<style scoped>
.example-section {
  display: grid;
  gap: 20px;
  padding: 56px 0;
  border-top: 1px solid var(--color-base-300);
  text-align: left;
  scroll-margin-top: 32px;
}

/*
 * One left edge for the whole section. The index used to hold a 40px grid
 * column, which indented the heading and the summary while the caption, the
 * demo, the code and the notes below them stayed flush left — the section read
 * as two misaligned columns. The index now sits in the margin beside the
 * content (and above it when the viewport is too narrow for a gutter), so the
 * title starts exactly where the body does.
 */
.example-head {
  position: relative;
  display: grid;
  gap: 4px;
}

.example-index {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.4;
  color: color-mix(in oklab, var(--color-base-content) 38%, transparent);
}

/*
 * The shell is 880px wide with 24px of padding, so a 40px gutter only clears
 * the viewport from about 1010px up; below that the index stacks above the
 * title rather than hanging off the edge of the screen.
 */
@media (min-width: 1024px) {
  .example-index {
    position: absolute;
    top: 2px;
    left: -40px;
    line-height: 2.2;
  }
}

.example-title {
  margin: 0 0 8px;
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.example-anchor {
  color: inherit;
  text-decoration: none;
}

.example-anchor:hover {
  text-decoration: underline;
  text-underline-offset: 4px;
}

.example-summary {
  margin: 0;
  max-width: 68ch;
  font-size: 15px;
  line-height: 1.65;
  color: color-mix(in oklab, var(--color-base-content) 68%, transparent);
}

.example-demo {
  display: grid;
  gap: 10px;
}

.example-demo-caption {
  margin: 0;
  font-size: 13px;
  color: color-mix(in oklab, var(--color-base-content) 56%, transparent);
}

.example-demo-stage {
  border: 1px solid var(--color-base-300);
  border-radius: 12px;
  padding: 20px;
  background: color-mix(in oklab, var(--color-base-200) 45%, transparent);
}

.example-note {
  margin: 0;
  max-width: 68ch;
  font-size: 13.5px;
  line-height: 1.6;
  color: color-mix(in oklab, var(--color-base-content) 58%, transparent);
}

.example-link {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  margin: 0;
  font-size: 13px;
}

.example-link a {
  color: var(--color-base-content);
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid color-mix(in oklab, var(--color-base-content) 30%, transparent);
}

.example-link a:hover {
  border-bottom-color: var(--color-base-content);
}

.example-link-url {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11.5px;
  color: color-mix(in oklab, var(--color-base-content) 42%, transparent);
  word-break: break-all;
}
</style>
